import React from "react";
import useLanguages from "@shared/hooks/common/useLanguages";
import { Timezone } from "@shared/types/commonTypes";
import { useTranslation } from "react-i18next";
import {
  Card,
  FormLayout,
  Select,
  BlockStack,
  Text,
  Box,
  SkeletonBodyText,
  SkeletonDisplayText,
} from "@medentic/polaris-react";
import { TimezoneAutoComplete } from "@shared/ui";

interface RegionalSettingsProps {
  timezone: Timezone | null;
  language: string;
  format: string;
  setTimezone: (timezone: Timezone | null) => void;
  setFormat: React.Dispatch<React.SetStateAction<string>>;
  setLanguage: React.Dispatch<React.SetStateAction<string>>;
  handleInputChange: (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string
  ) => void;
}

const RegionalSettings: React.FC<RegionalSettingsProps> = ({
  timezone,
  language,
  format,
  setFormat,
  setTimezone,
  setLanguage,
  handleInputChange,
}) => {
  const { t } = useTranslation();
  const { languages, isLoading: isLanguagesLoading } = useLanguages();

  return (
    <Card>
      <BlockStack>
        {isLanguagesLoading ? (
          <BlockStack gap={"400"}>
            <BlockStack gap={"200"}>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={2} />
            </BlockStack>
            <BlockStack gap={"200"}>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={2} />
            </BlockStack>
            <BlockStack gap={"200"}>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={2} />
            </BlockStack>
          </BlockStack>
        ) : (
          <>
            <Text as="h1" variant="headingMd">
              {t("regionalSettings.title")}
            </Text>
            <Box paddingBlock="200">
              <Text as="p" tone="subdued">
                {t("regionalSettings.description")}
              </Text>
            </Box>
            <FormLayout>
              <FormLayout.Group>
                <TimezoneAutoComplete
                  label={t("regionalSettings.timeZone")}
                  value={timezone}
                  onChange={setTimezone}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Select
                  label={t("regionalSettings.preferredLanguage")}
                  options={languages.map((lang) => ({
                    label: lang.localLabel,
                    value: lang.iso,
                  }))}
                  value={language}
                  onChange={(value) =>
                    handleInputChange(setLanguage, value ?? "")
                  }
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Select
                  label={t("regionalSettings.format")}
                  options={languages.map((lang) => ({
                    label: lang.englishLabel,
                    value: lang.iso,
                  }))}
                  value={format}
                  onChange={(value) => handleInputChange(setFormat, value ?? "")}
                />
              </FormLayout.Group>
            </FormLayout>
          </>
        )}
      </BlockStack>
    </Card>
  );
};

export default RegionalSettings;
